<div class="gsea-container margin-top-2">
  <div class="header-section">
    <button
      nbButton
      ghost
      status="primary"
      size="small"
      (click)="goBack()"
      class="back-button"
    >
      <nb-icon icon="arrow-back-outline"></nb-icon>
      Back to Analysis Tools
    </button>
    <h2 class="page-title">Pathway Analysis</h2>
    <p class="page-subtitle">Gene Set Enrichment using MAGMA</p>
  </div>

  <ngx-spinner
    *ngIf="isLoadingEndpoints || isAnalyzing"
    color="#D73647"
    bdColor="rgba(0,0,0,0.5)"
    [fullScreen]="false"
    size="medium"
  >
    <p *ngIf="isLoadingEndpoints">Loading available endpoints...</p>
    <p *ngIf="isAnalyzing">Running gene set enrichment...</p>
  </ngx-spinner>

  <div *ngIf="!isLoadingEndpoints" class="analysis-form">
    <nb-card>
      <nb-card-header>
        <nb-icon icon="bulb-outline"></nb-icon>
        Configure Analysis
      </nb-card-header>
      <nb-card-body>
        <!-- Endpoint Selection -->
        <div class="form-group">
          <label for="endpoint-select" class="form-label">
            1. Select GWAS Endpoint
          </label>
          <nb-select
            id="endpoint-select"
            placeholder="Choose an endpoint..."
            [(selected)]="selectedEndpoint"
            fullWidth
            [disabled]="!magmaAvailable"
          >
            <nb-option *ngFor="let ep of endpoints" [value]="ep.name">
              {{ ep.display_name }}
            </nb-option>
          </nb-select>
        </div>

        <!-- File Upload -->
        <div class="form-group">
          <label class="form-label">
            2. Upload Gene Set File
            <button
              nbButton
              ghost
              size="tiny"
              status="info"
              (click)="toggleFormatHelp()"
              class="help-button"
            >
              <nb-icon icon="question-mark-circle-outline"></nb-icon>
              Format Help
            </button>
          </label>

          <!-- Format Help Panel -->
          <div *ngIf="showFormatHelp" class="format-help">
            <h4>Gene Set File Format</h4>
            <p>One gene set per line, with set name followed by gene identifiers:</p>
            <pre>
# Tab-separated (recommended)
SET_NAME&#9;MLH1&#9;MSH3&#9;FAN1

# Comma-separated
SET_NAME,MLH1,MSH3,FAN1

# Space-separated
SET_NAME MLH1 MSH3 FAN1

# Ensembl IDs also work
SET_NAME&#9;ENSG00000076242&#9;ENSG00000095002</pre>
            <ul>
              <li>First column: Gene set name</li>
              <li>Remaining columns: Gene symbols (e.g., MLH1) or Ensembl IDs (ENSG format)</li>
              <li>Supported delimiters: tab, comma, or space</li>
              <li>UTF-8 encoded text file (.txt, .tsv, or .csv)</li>
            </ul>
          </div>

          <!-- Drop Zone -->
          <div
            class="drop-zone"
            [class.has-file]="selectedFile"
            [class.disabled]="!magmaAvailable"
            (drop)="onFileDrop($event)"
            (dragover)="onDragOver($event)"
            (click)="fileInput.click()"
          >
            <input
              #fileInput
              type="file"
              accept=".txt,.tsv,.csv,text/plain,text/tab-separated-values,text/csv"
              (change)="onFileSelected($event)"
              hidden
            />

            <div *ngIf="!selectedFile" class="drop-zone-content">
              <nb-icon icon="cloud-upload-outline" class="upload-icon"></nb-icon>
              <p>Drag & drop your gene set file here</p>
              <p class="or-text">or click to browse</p>
            </div>

            <div *ngIf="selectedFile" class="file-info">
              <nb-icon icon="file-text-outline" class="file-icon"></nb-icon>
              <span class="file-name">{{ fileName }}</span>
              <button
                nbButton
                ghost
                size="small"
                status="danger"
                (click)="clearFile(); $event.stopPropagation()"
              >
                <nb-icon icon="close-outline"></nb-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Error Display -->
        <div *ngIf="error" class="alert alert-error">
          <nb-icon icon="alert-circle-outline"></nb-icon>
          <span>{{ error.message }}</span>
        </div>

        <!-- Warnings Display -->
        <div *ngIf="warnings.length > 0" class="alert alert-warning">
          <nb-icon icon="alert-triangle-outline"></nb-icon>
          <div class="warning-content">
            <span *ngFor="let warning of warnings">{{ warning }}</span>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button
            nbButton
            status="primary"
            size="medium"
            [disabled]="!canSubmit()"
            (click)="submitAnalysis()"
          >
            <nb-icon icon="flash-outline"></nb-icon>
            Run Analysis
          </button>
        </div>
      </nb-card-body>
    </nb-card>

    <!-- Enrichment Plot Results -->
    <nb-card *ngIf="analysisResult" class="results-card">
      <nb-card-header>
        <nb-icon icon="bar-chart-outline"></nb-icon>
        Enrichment Results
        <span class="results-subtitle" *ngIf="analysisResult.total_gene_sets > 0">
          Top {{ analysisResult.plots.length }} of
          {{ analysisResult.total_gene_sets }} gene sets
        </span>
        <button
          nbButton
          status="primary"
          size="small"
          [disabled]="isDownloading"
          (click)="downloadResults()"
          class="download-button"
        >
          <nb-icon icon="download-outline"></nb-icon>
          {{ isDownloading ? 'Downloading...' : 'Download Full Results (ZIP)' }}
        </button>
      </nb-card-header>
      <nb-card-body>
        <div *ngIf="analysisResult.plots.length === 0" class="no-results">
          <p>No gene sets had sufficient overlap with the ranked gene list to generate plots.</p>
          <p>Results are still available for download.</p>
        </div>

        <div class="plots-container">
          <div
            *ngFor="let plot of analysisResult.plots; let i = index"
            class="plot-item"
            [class.significant]="isSignificant(plot.p_value)"
            [class.not-significant]="!isSignificant(plot.p_value)"
          >
            <div class="plot-meta">
              <span class="plot-rank">#{{ i + 1 }}</span>
              <span class="plot-setname">{{ plot.set_name }}</span>
              <span
                class="plot-pvalue"
                [class.pvalue-significant]="isSignificant(plot.p_value)"
                [class.pvalue-ns]="!isSignificant(plot.p_value)"
              >
                p = {{ formatPValue(plot.p_value) }}
              </span>
              <span
                *ngIf="!isSignificant(plot.p_value)"
                class="ns-badge"
              >
                Not significant
              </span>
              <span class="plot-genecounts">
                {{ plot.n_genes_ranked }} / {{ plot.n_genes_in_set }} genes matched
              </span>
            </div>
            <div *ngIf="plot.plot_b64; else noPlot" class="plot-image-wrapper">
              <img
                [src]="getPlotSrc(plot.plot_b64)"
                [alt]="'Enrichment plot for ' + plot.set_name"
                class="enrichment-plot"
              />
            </div>
            <ng-template #noPlot>
              <div class="no-plot-message">
                No hits found in ranked gene list for this set.
              </div>
            </ng-template>

            <!-- Gene Table -->
            <div *ngIf="plot.genes && plot.genes.length > 0" class="gene-table-wrapper">
              <table class="gene-table">
                <thead>
                  <tr>
                    <th>Gene</th>
                    <th>Chr</th>
                    <th>Start</th>
                    <th>Stop</th>
                    <th>SNPs</th>
                    <th>ZSTAT</th>
                    <th>P-value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let gene of plot.genes">
                    <td class="gene-name">{{ gene.gene }}</td>
                    <td>{{ gene.chr }}</td>
                    <td>{{ gene.start | number }}</td>
                    <td>{{ gene.stop | number }}</td>
                    <td>{{ gene.nsnps }}</td>
                    <td class="zstat-cell">{{ gene.zstat.toFixed(3) }}</td>
                    <td class="pvalue-cell">{{ formatPValue(gene.p) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <p
          *ngIf="analysisResult.total_gene_sets > analysisResult.plots.length"
          class="truncation-note"
        >
          <nb-icon icon="info-outline"></nb-icon>
          Showing top {{ analysisResult.plots.length }} of
          {{ analysisResult.total_gene_sets }} gene sets by significance.
          All plots are available in the download.
        </p>
      </nb-card-body>
    </nb-card>

    <!-- Info Card -->
    <nb-card class="info-card">
      <nb-card-header>
        <nb-icon icon="info-outline"></nb-icon>
        About Gene Set Enrichment
      </nb-card-header>
      <nb-card-body>
        <p>
          Gene Set Enrichment tests whether your custom gene sets show
          enrichment in our GWAS results using
          <a href="https://journals.plos.org/ploscompbiol/article?id=10.1371/journal.pcbi.1004219"
             target="_blank" rel="noopener noreferrer">MAGMA</a>
          (Multi-marker Analysis of GenoMic Annotation).
        </p>
        <p>
          <strong>Output:</strong> Enrichment plots showing where your gene set members
          fall in the GWAS-ranked gene list, plus a ZIP file with statistical results
          including p-values, effect sizes (beta), and per-gene details.
        </p>
        <p class="note">
          <nb-icon icon="clock-outline"></nb-icon>
          Analysis typically takes 10-30 seconds depending on the number of gene
          sets.
        </p>
      </nb-card-body>
    </nb-card>
  </div>
</div>
